import { ArrowDown, ArrowUp, Coins, Eye, FileSpreadsheet, Flag, ImageDown, Play, Plus, RotateCcw, Settings2, SkipForward, Trash2, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import {
  CHANGE,
  POOL,
  REDIS_LIMITS,
  canMove,
  coinValues,
  countOf,
  pickCoins,
  totalOf,
  totalToShare,
  type RedisCircle,
  type RedisCoinKind,
  type RedisSetup,
  type RedistributionState,
} from '../../shared/redistribution';
import { Button } from '../components/ui';
import { boardImage, coinStyle, excelFile, fmt, percent, results, saveFile, type CoinShape } from '../lib/redistribution';
import { useStore } from '../lib/store';
import { toast } from '../lib/toast';
import type { MeetingSession } from '../meeting/MeetingSession';

// ───────────────────────────── host: preparing it ─────────────────────────────

interface Draft {
  title: string;
  circles: RedisCircle[];
  coins: RedisCoinKind[];
  change: RedisCoinKind[];
  turnOrder: string[];
}

const LAST_KEY = 'nexo.redistribution.last';
let idSeq = 0;
const newCircleId = () => `c${Date.now().toString(36)}${(idSeq++).toString(36)}`;

/** The last setup on this device (names and coins), so the next redistribution starts from it. */
function loadLast(): Omit<Draft, 'turnOrder'> | null {
  try {
    const raw = localStorage.getItem(LAST_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw) as Omit<Draft, 'turnOrder'>;
    return { title: d.title, circles: d.circles.map((c) => ({ id: newCircleId(), name: c.name })), coins: d.coins, change: d.change };
  } catch {
    return null;
  }
}

function saveLast(d: Partial<Draft>): void {
  try {
    const before = JSON.parse(localStorage.getItem(LAST_KEY) ?? '{}') as Partial<Draft>;
    const { turnOrder: _t, ...keep } = { ...before, ...d };
    localStorage.setItem(LAST_KEY, JSON.stringify(keep));
  } catch {
    /* private mode: not remembered */
  }
}

const sumOf = (kinds: RedisCoinKind[]) => kinds.reduce((n, k) => n + k.value * k.count, 0);

/** Host's panel: circles (any names), coins, change and who takes turns, in what order. */
export function RedistributionPanel({ session, onClose }: { session: MeetingSession; onClose(): void }) {
  const s = useStore(session.store);
  const r = s.redistribution;
  const people = Object.values(s.everyone).sort((a, b) => a.joinedAt - b.joinedAt);
  const [draft, setDraft] = useState<Draft>(() => {
    if (r.status !== 'off') return { title: r.title, circles: r.circles, coins: [], change: [], turnOrder: r.turnOrder };
    const last = loadLast();
    return {
      title: last?.title ?? 'Redistribución',
      circles: last?.circles ?? [],
      coins: last?.coins ?? [
        { value: 10, count: 10 },
        { value: 5, count: 10 },
      ],
      change: last?.change ?? [{ value: 1, count: 20 }],
      turnOrder: people.map((p) => p.id),
    };
  });
  const [confirmRestart, setConfirmRestart] = useState(false);
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const editingLive = r.status !== 'off' && !confirmRestart;

  const names = draft.circles.map((c) => c.name.trim());
  const problems = [
    !draft.circles.length && 'Añade al menos un círculo.',
    names.some((n) => !n) && 'Escribe el nombre de cada círculo.',
    !editingLive && sumOf(draft.coins) === 0 && 'Indica cuántas fichas hay que repartir.',
    !editingLive && [...draft.coins, ...draft.change].reduce((n, k) => n + k.count, 0) > REDIS_LIMITS.coins && `Como máximo ${REDIS_LIMITS.coins} fichas en total.`,
  ].filter(Boolean) as string[];

  const addPeople = () => {
    const have = new Set(names.map((n) => n.toLowerCase()));
    const extra = people.filter((p) => !have.has(p.name.toLowerCase())).map((p) => ({ id: newCircleId(), name: p.name }));
    set({ circles: [...draft.circles, ...extra].slice(0, REDIS_LIMITS.circles) });
  };

  const setup = (): RedisSetup => ({
    title: draft.title.trim() || 'Redistribución',
    circles: draft.circles.map((c) => ({ id: c.id, name: c.name.trim() })),
    coins: draft.coins.filter((k) => k.count > 0),
    change: draft.change.filter((k) => k.count > 0),
    turnOrder: draft.turnOrder.filter((id) => s.everyone[id]),
  });

  const start = () => {
    saveLast(draft);
    session.redistribute({ op: 'start', setup: setup() });
    setConfirmRestart(false);
    onClose();
  };

  const saveEdit = () => {
    const { title, circles, turnOrder } = setup();
    session.redistribute({ op: 'edit', setup: { title, circles, turnOrder } });
    saveLast({ title, circles });
    toast('Cambios guardados', 'success');
  };

  // Turn order: people who take turns, in order, then the rest.
  const inOrder = draft.turnOrder.filter((id) => s.everyone[id]);
  const outOfOrder = people.filter((p) => !inOrder.includes(p.id));
  const moveTurn = (id: string, delta: number) => {
    const list = [...inOrder];
    const i = list.indexOf(id);
    const j = i + delta;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    set({ turnOrder: list });
  };

  return (
    <section className="panel redis-panel" aria-label="Redistribución">
      <div className="panel-header">
        <span>Redistribución</span>
        <button type="button" className="icon-btn" aria-label="Cerrar redistribución" onClick={onClose}>
          <X size={18} />
        </button>
      </div>
      <div className="panel-body">
        <p className="hint">
          {editingLive
            ? 'La redistribución está en curso: puedes cambiar nombres, añadir o quitar círculos (sus fichas vuelven al fondo común) y el orden de turnos.'
            : 'Cada persona, en su turno, mueve fichas al círculo que quiera. Siguen las rondas hasta que el grupo está de acuerdo.'}
        </p>

        <label className="field">
          <span>Título</span>
          <input className="input" value={draft.title} maxLength={REDIS_LIMITS.title} onChange={(e) => set({ title: e.target.value })} aria-label="Título de la redistribución" />
        </label>

        <h3 className="redis-h">
          Círculos <span className="bo-count">{draft.circles.length}</span>
        </h3>
        <p className="hint">Los nombres que quieras; no hace falta que esas personas estén en la llamada.</p>
        <div className="redis-list">
          {draft.circles.map((c, i) => (
            <div key={c.id} className="redis-row">
              <input
                className="input"
                value={c.name}
                maxLength={REDIS_LIMITS.name}
                placeholder={`Nombre ${i + 1}`}
                aria-label={`Nombre del círculo ${i + 1}`}
                onChange={(e) => set({ circles: draft.circles.map((x) => (x.id === c.id ? { ...x, name: e.target.value } : x)) })}
              />
              <button type="button" className="icon-btn" aria-label={`Quitar ${c.name || `círculo ${i + 1}`}`} onClick={() => set({ circles: draft.circles.filter((x) => x.id !== c.id) })}>
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
        <div className="bo-tools">
          <Button size="sm" variant="ghost" disabled={draft.circles.length >= REDIS_LIMITS.circles} onClick={() => set({ circles: [...draft.circles, { id: newCircleId(), name: '' }] })}>
            <Plus size={14} /> Añadir círculo
          </Button>
          <Button size="sm" variant="ghost" onClick={addPeople}>
            <Plus size={14} /> Añadir a quienes están en la llamada
          </Button>
        </div>

        {!editingLive && (
          <>
            <h3 className="redis-h">Fichas para repartir</h3>
            <CoinKinds kinds={draft.coins} onChange={(coins) => set({ coins })} label="repartir" />
            <p className="hint">
              Total: <strong>{fmt(sumOf(draft.coins))}</strong> en {draft.coins.reduce((n, k) => n + k.count, 0)} fichas
            </p>
            <h3 className="redis-h">Fichas para dar cambio</h3>
            <p className="hint">Se cambian por fichas grandes (y al revés). No se reparten.</p>
            <CoinKinds kinds={draft.change} onChange={(change) => set({ change })} label="cambio" />
          </>
        )}

        <h3 className="redis-h">Turnos</h3>
        <p className="hint">Juegan quienes están en la llamada, en este orden. Si alguien no está cuando le toca, se salta.</p>
        <ol className="redis-turns">
          {inOrder.map((id, i) => (
            <li key={id} className="redis-row">
              <label className="check">
                <input type="checkbox" checked onChange={() => set({ turnOrder: inOrder.filter((x) => x !== id) })} aria-label={`${s.everyone[id].name} juega`} />
                <span>
                  {i + 1}. {s.everyone[id].name}
                  {id === s.selfId ? ' (Yo)' : ''}
                </span>
              </label>
              <button type="button" className="icon-btn" aria-label={`Subir a ${s.everyone[id].name}`} disabled={i === 0} onClick={() => moveTurn(id, -1)}>
                <ArrowUp size={15} />
              </button>
              <button type="button" className="icon-btn" aria-label={`Bajar a ${s.everyone[id].name}`} disabled={i === inOrder.length - 1} onClick={() => moveTurn(id, 1)}>
                <ArrowDown size={15} />
              </button>
            </li>
          ))}
          {outOfOrder.map((p) => (
            <li key={p.id} className="redis-row redis-out">
              <label className="check">
                <input type="checkbox" checked={false} onChange={() => set({ turnOrder: [...inOrder, p.id] })} aria-label={`${p.name} juega`} />
                <span>{p.name} (no juega)</span>
              </label>
            </li>
          ))}
        </ol>
        {!inOrder.length && <p className="hint">Nadie tiene turno: solo tú moverás las fichas.</p>}
        {problems.length > 0 && <p className="callout callout-warning">{problems[0]}</p>}
        {confirmRestart && <p className="callout callout-warning">Empezar de nuevo devuelve todas las fichas al fondo común con las cantidades de arriba.</p>}
      </div>
      <div className="panel-footer">
        {editingLive ? (
          <>
            <Button variant="ghost" onClick={() => setConfirmRestart(true)}>
              <RotateCcw size={15} /> Empezar de nuevo
            </Button>
            <Button variant="primary" disabled={problems.length > 0} onClick={saveEdit}>
              Guardar cambios
            </Button>
          </>
        ) : (
          <>
            {confirmRestart && (
              <Button variant="ghost" onClick={() => setConfirmRestart(false)}>
                Cancelar
              </Button>
            )}
            <Button variant="primary" disabled={problems.length > 0} onClick={start}>
              <Play size={15} /> {r.status === 'off' ? 'Empezar redistribución' : 'Empezar de nuevo'}
            </Button>
          </>
        )}
      </div>
    </section>
  );
}

function CoinKinds({ kinds, onChange, label }: { kinds: RedisCoinKind[]; onChange(k: RedisCoinKind[]): void; label: string }) {
  const update = (i: number, patch: Partial<RedisCoinKind>) => onChange(kinds.map((k, j) => (j === i ? { ...k, ...patch } : k)));
  const num = (v: string, min: number, max: number) => Math.max(min, Math.min(max, Math.floor(Number(v) || 0)));
  return (
    <div className="redis-list">
      {kinds.map((k, i) => (
        <div key={i} className="redis-row redis-kind">
          <input className="input" type="number" min={0} max={500} value={k.count} aria-label={`Cuántas fichas (${label}) de ${k.value}`} onChange={(e) => update(i, { count: num(e.target.value, 0, 500) })} />
          <span>fichas de</span>
          <input className="input" type="number" min={1} max={REDIS_LIMITS.value} value={k.value} aria-label={`Valor de las fichas (${label}) ${i + 1}`} onChange={(e) => update(i, { value: num(e.target.value, 1, REDIS_LIMITS.value) })} />
          <button type="button" className="icon-btn" aria-label={`Quitar fichas de ${k.value} (${label})`} onClick={() => onChange(kinds.filter((_, j) => j !== i))}>
            <Trash2 size={15} />
          </button>
        </div>
      ))}
      {kinds.length < REDIS_LIMITS.kinds && (
        <Button size="sm" variant="ghost" onClick={() => onChange([...kinds, { value: 1, count: 0 }])}>
          <Plus size={14} /> Otro tipo de ficha
        </Button>
      )}
    </div>
  );
}

// ───────────────────────────── the board ─────────────────────────────

function Coin({ shape, color, ink, value, size = 34 }: { shape: CoinShape; color: string; ink: string; value: number; size?: number }) {
  const r = 15;
  let path: string;
  if (shape === 'circle') path = `M 2 17 A ${r} ${r} 0 1 0 32 17 A ${r} ${r} 0 1 0 2 17 Z`;
  else if (shape === 'square') path = 'M 4 4 H 30 V 30 H 4 Z';
  else {
    const points = shape === 'triangle' ? 3 : shape === 'diamond' ? 4 : shape === 'hexagon' ? 6 : 10;
    const pts = Array.from({ length: points }, (_, i) => {
      const a = -Math.PI / 2 + (i * 2 * Math.PI) / points;
      const rr = shape === 'star' && i % 2 ? 7 : shape === 'triangle' ? 18 : 16;
      return `${17 + rr * Math.cos(a)},${(shape === 'triangle' ? 21 : 17) + rr * Math.sin(a)}`;
    });
    path = `M ${pts.join(' L ')} Z`;
  }
  const label = fmt(value);
  return (
    <svg width={size} height={size} viewBox="0 0 34 34" aria-hidden="true" className="coin-svg">
      <path d={path} fill={color} />
      <text x="17" y={shape === 'triangle' ? 24 : 18} textAnchor="middle" dominantBaseline="middle" fill={ink} fontSize={label.length > 3 ? 8 : label.length > 2 ? 10 : 12} fontWeight="700">
        {label}
      </text>
    </svg>
  );
}

function useSize<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setSize({ width: entry.contentRect.width, height: entry.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size] as const;
}

type Pick = { place: string; value: number };
type Drag = Pick & { x: number; y: number; over: string | null };

const placeAt = (x: number, y: number): string | null => (document.elementFromPoint(x, y)?.closest('[data-place]') as HTMLElement | null)?.dataset.place ?? null;

/** The shared board: everyone sees it live; whoever has the turn drags (or taps) coins between places. */
export function RedistributionBoard({ session, onConfigure, onHide }: { session: MeetingSession; onConfigure(): void; onHide(): void }) {
  const s = useStore(session.store);
  const r = s.redistribution;
  const me = s.everyone[s.selfId!];
  const amHost = me?.role === 'host';
  const present = (id: string) => !!s.everyone[id]?.connected;
  const mayMove = !!me && canMove(r, { id: me.id, name: me.name, isHost: amHost }, { present });
  const values = coinValues(r.holdings);
  const total = totalToShare(r.holdings);
  const [picked, setPicked] = useState<Pick | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const press = useRef<(Pick & { x: number; y: number; id: number }) | null>(null);
  const suppressClick = useRef(false);
  const [areaRef, area] = useSize<HTMLDivElement>();
  const [busy, setBusy] = useState(false);

  // A selection that no longer makes sense (turn passed, coin gone) is dropped.
  useEffect(() => {
    if (picked && (!mayMove || countOf(r.holdings, picked.place, picked.value) < 1)) setPicked(null);
  }, [picked, mayMove, r.holdings]);

  const move = (from: string, to: string, value: number) => {
    if (from === to || to === CHANGE) return;
    session.redistribute({ op: 'move', from, to, value });
  };

  const onDown = (e: ReactPointerEvent<HTMLButtonElement>, place: string, value: number) => {
    if (!mayMove || place === CHANGE || e.button > 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    press.current = { place, value, x: e.clientX, y: e.clientY, id: e.pointerId };
  };
  const onMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const p = press.current;
    if (!p || p.id !== e.pointerId) return;
    if (!drag && Math.hypot(e.clientX - p.x, e.clientY - p.y) < 6) return;
    setDrag({ place: p.place, value: p.value, x: e.clientX, y: e.clientY, over: placeAt(e.clientX, e.clientY) });
  };
  const onUp = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const p = press.current;
    press.current = null;
    if (!p || !drag) return;
    suppressClick.current = true;
    const to = placeAt(e.clientX, e.clientY);
    setDrag(null);
    setPicked(null);
    if (to) move(p.place, to, p.value);
  };

  const onChipClick = (place: string, value: number) => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    if (!mayMove) return void toast(r.status === 'running' ? 'Espera tu turno para mover fichas' : 'La redistribución no está en curso', 'info');
    if (place === CHANGE) return void toast('Las fichas del cambio se usan con «Dar cambio» o «Juntar»', 'info');
    setPicked((cur) => (cur && cur.place === place && cur.value === value ? null : { place, value }));
  };

  const dropOn = (to: string) => {
    if (!picked || to === picked.place) return;
    move(picked.place, to, picked.value);
    // Keep the selection while the place has more coins of that value: tap, tap, tap.
    if (countOf(r.holdings, picked.place, picked.value) <= 1) setPicked(null);
  };

  const exportAs = async (kind: 'excel' | 'image') => {
    setBusy(true);
    try {
      await saveFile(kind === 'excel' ? excelFile(r) : await boardImage(r));
    } catch (err) {
      toast(`No se pudo guardar: ${(err as Error).message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const turnName = r.turnOf ? (r.turnOf === me?.id ? 'ti' : (s.everyone[r.turnOf]?.name ?? 'alguien')) : null;
  const turnAbsent = !!r.turnOf && !present(r.turnOf);
  const ring = r.circles.length <= 12 && area.width >= 640 && area.height >= 430;

  const place = (id: string, name: string, kind: 'circle' | 'pool' | 'change', style?: CSSProperties) => {
    const sum = totalOf(r.holdings, id);
    const chips = values.filter((v) => countOf(r.holdings, id, v) > 0);
    const target = (picked && picked.place !== id && kind !== 'change') || (drag && drag.place !== id && kind !== 'change');
    return (
      <div
        key={id}
        className={`redis-place redis-${kind} ${target ? 'is-target' : ''} ${drag?.over === id && drag.place !== id && kind !== 'change' ? 'is-over' : ''}`}
        style={style}
        data-place={kind === 'change' ? undefined : id}
        role="group"
        aria-label={`${name}: ${fmt(sum)}`}
        onClick={() => target && dropOn(id)}
      >
        <div className="redis-disc">
          <span className="redis-name">{name}</span>
          <span className="redis-sum">{fmt(sum)}</span>
        </div>
        {kind === 'circle' && total > 0 && <span className="redis-pct">{percent(sum, total)}</span>}
        <div className="redis-chips">
          {chips.map((v) => {
            const st = coinStyle(values, v);
            const isPicked = picked?.place === id && picked.value === v;
            return (
              <button
                key={v}
                type="button"
                className={`redis-chip ${isPicked ? 'is-picked' : ''}`}
                aria-label={`Ficha de ${fmt(v)} en ${name} (${countOf(r.holdings, id, v)})`}
                aria-pressed={isPicked}
                disabled={kind === 'change' && !mayMove}
                onPointerDown={(e) => onDown(e, id, v)}
                onPointerMove={onMove}
                onPointerUp={onUp}
                onPointerCancel={() => {
                  press.current = null;
                  setDrag(null);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onChipClick(id, v);
                }}
              >
                <Coin {...st} value={v} size={kind === 'change' ? 26 : 32} />
                <span className="redis-count">×{countOf(r.holdings, id, v)}</span>
              </button>
            );
          })}
        </div>
        {target && picked && (
          <button
            type="button"
            className="redis-here"
            onClick={(e) => {
              e.stopPropagation();
              dropOn(id);
            }}
          >
            Poner aquí
          </button>
        )}
      </div>
    );
  };

  // Change: break the picked coin into smaller ones, or join smaller coins of that place into a bigger one.
  const canBreak = picked && pickCoins(r.holdings[CHANGE] ?? {}, picked.value, picked.value);
  const joins = picked
    ? values.filter((v) => v > picked.value && countOf(r.holdings, CHANGE, v) > 0 && pickCoins(r.holdings[picked.place] ?? {}, v, v))
    : [];

  return (
    <div className="redis-board" aria-label="Tablero de redistribución">
      <header className="redis-head">
        <div className="redis-title">
          <Coins size={20} />
          <div>
            <strong>{r.title || 'Redistribución'}</strong>
            <small>
              {r.status === 'finished' ? 'Terminada' : `Ronda ${r.round}`} · {fmt(total)} en total
            </small>
          </div>
        </div>
        <div className="redis-turn" role="status" aria-live="polite">
          {r.status === 'running' &&
            (turnName ? (
              <span className={r.turnOf === me?.id ? 'is-mine' : ''}>
                {r.turnOf === me?.id ? 'Te toca a ti' : `Le toca a ${turnName}`}
                {turnAbsent && ' (no está conectado)'}
              </span>
            ) : (
              <span>Nadie tiene el turno{amHost ? ': mueves tú' : ''}</span>
            ))}
          {r.status === 'finished' && <span>Redistribución terminada</span>}
        </div>
        <div className="redis-actions">
          {r.status === 'running' && r.turnOf === me?.id && (
            <Button variant="primary" size="sm" onClick={() => session.redistribute({ op: 'pass' })}>
              <SkipForward size={15} /> Paso: terminar mi turno
            </Button>
          )}
          {amHost && r.status === 'running' && r.turnOf !== me?.id && (
            <Button size="sm" onClick={() => session.redistribute({ op: 'pass' })}>
              <SkipForward size={15} /> Pasar turno
            </Button>
          )}
          {amHost && r.status === 'running' && (
            <Button size="sm" variant="danger" onClick={() => session.redistribute({ op: 'finish' })}>
              <Flag size={15} /> Terminar
            </Button>
          )}
          {amHost && r.status === 'finished' && (
            <Button size="sm" onClick={() => session.redistribute({ op: 'resume' })}>
              <Play size={15} /> Reanudar
            </Button>
          )}
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => void exportAs('excel')}>
            <FileSpreadsheet size={15} /> Excel
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => void exportAs('image')}>
            <ImageDown size={15} /> Imagen
          </Button>
          {amHost && (
            <button type="button" className="icon-btn" aria-label="Configurar la redistribución" title="Configurar" onClick={onConfigure}>
              <Settings2 size={17} />
            </button>
          )}
          <button type="button" className="icon-btn" aria-label="Ver los vídeos" title="Ver los vídeos" onClick={onHide}>
            <Eye size={17} />
          </button>
        </div>
      </header>
      {r.last && <p className="redis-last">{r.last}</p>}

      <div ref={areaRef} className={`redis-area ${ring ? 'is-ring' : 'is-grid'}`}>
        {area.width > 0 &&
          (ring ? (
            <>
              {place(CHANGE, 'Cambio', 'change')}
              {place(POOL, 'Fondo común', 'pool', { left: '50%', top: '50%' })}
              {r.circles.map((c, i) => {
                const a = -Math.PI / 2 + (i * 2 * Math.PI) / r.circles.length;
                return place(c.id, c.name, 'circle', { left: `${50 + 37 * Math.cos(a)}%`, top: `${50 + 35 * Math.sin(a)}%` });
              })}
            </>
          ) : (
            <>
              {place(POOL, 'Fondo común', 'pool')}
              {r.circles.map((c) => place(c.id, c.name, 'circle'))}
              {place(CHANGE, 'Cambio', 'change')}
            </>
          ))}
      </div>

      {picked && (
        <div className="redis-picked" role="toolbar" aria-label="Ficha elegida">
          <span>
            Ficha de <strong>{fmt(picked.value)}</strong> de {picked.place === POOL ? 'el fondo común' : (r.circles.find((c) => c.id === picked.place)?.name ?? '')}: toca dónde ponerla
          </span>
          {canBreak && (
            <Button size="sm" onClick={() => session.redistribute({ op: 'break', place: picked.place, value: picked.value })}>
              Dar cambio
            </Button>
          )}
          {joins.map((v) => (
            <Button key={v} size="sm" onClick={() => session.redistribute({ op: 'join', place: picked.place, value: v })}>
              Juntar en una de {fmt(v)}
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={() => setPicked(null)}>
            Cancelar
          </Button>
        </div>
      )}

      {r.status === 'finished' && <ResultTable r={r} />}

      {drag && (
        <div className="redis-ghost" style={{ left: drag.x, top: drag.y }} aria-hidden="true">
          <Coin {...coinStyle(values, drag.value)} value={drag.value} size={40} />
        </div>
      )}
    </div>
  );
}

function ResultTable({ r }: { r: RedistributionState }) {
  const res = results(r);
  return (
    <div className="redis-results">
      <table aria-label="Resultado de la redistribución">
        <thead>
          <tr>
            <th>Nombre</th>
            {res.values.map((v) => (
              <th key={v}>Fichas de {fmt(v)}</th>
            ))}
            <th>Total</th>
            <th>%</th>
          </tr>
        </thead>
        <tbody>
          {[...res.rows, ...(res.pool.total ? [res.pool] : [])].map((row) => (
            <tr key={row.name}>
              <td>{row.name}</td>
              {row.counts.map((n, i) => (
                <td key={i}>{n}</td>
              ))}
              <td>
                <strong>{fmt(row.total)}</strong>
              </td>
              <td>{percent(row.total, res.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Line on top of the stage while a redistribution is on: what is happening, show/hide the board. */
export function RedistributionBar({ session, boardShown, onToggleBoard }: { session: MeetingSession; boardShown: boolean; onToggleBoard(): void }) {
  const s = useStore(session.store);
  const r = s.redistribution;
  if (r.status === 'off' || boardShown) return null;
  const mine = r.status === 'running' && r.turnOf === s.selfId;
  return (
    <div className="breakout-bar redis-bar" role="toolbar" aria-label="Redistribución">
      <span className="bo-here">
        <Coins size={15} /> {r.title || 'Redistribución'}
        {r.status === 'finished' ? ' · terminada' : mine ? ' · te toca a ti' : ''}
      </span>
      <button type="button" className="share-bar-btn" onClick={onToggleBoard}>
        Ver tablero
      </button>
    </div>
  );
}
